import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../../api/apiFetch";
import { fetchDashboard } from "../../../api/dashboard";
import { BEHAVIOR_CHANGED_EVENT } from "../../behavior/utils/invalidation";
import { buildDominantInterventionViewModel } from "../../../intervention/buildDominantInterventionViewModel";
import { blockUser } from "../../../api/moderation";
import {
  getPartnerActedToday,
  getPartnerLastActivityAt,
  getSprintWindow,
  getViewerActedToday,
} from "../../shared/selectors/behaviorSummary";
import {
  closeTrustBudRelationship,
  fetchSprintPartnerProfile,
  disconnectTrustBudRelationship,
  fetchSprintReflectionToday,
  fetchSprintReflectionWeek,
  fetchAccountabilityDailyReport,
  submitAccountabilityPhotoProof,
  submitAccountabilityTimerReport,
  acknowledgeAccountabilityReport,
  renewTrustBudRelationship,
  submitChallengeShowUp,
} from "../../../api/trustbuds";
import { normalizeCanonicalSummary } from "../../behavior/utils/canonicalSummary";
import {
  buildMomentumRingData,
  buildRecentMovementItems,
  buildSprintQuickReplies,
  buildWeekPattern,
  formatDateLabel,
  formatRelativeDayWord,
  resolveSprintStateCopy,
} from "../utils/resolveSprintStateCopy";

function getRelationshipKey(item) {
  return String(
    item?.connection?.sprint_id ||
      item?.connection?.network_connection_id ||
      item?.connection?.id ||
      item?.id ||
      item?.other_user?.id ||
      ""
  );
}

function isSprintWorkspaceCandidate(item) {
  if (!item) return false;
  if (item?.active_sprint?.id) return true;
  return false;
}

function isInterventionTarget(item, intervention) {
  if (!item || !intervention) return false;
  const partnerId = intervention?.partner_summary?.id;
  const sprintId = item?.active_sprint?.id || null;
  if (partnerId && Number(item?.other_user?.id) === Number(partnerId)) {
    return true;
  }
  if (
    intervention?.supporting_context?.partner_user_id &&
    Number(item?.other_user?.id) === Number(intervention.supporting_context.partner_user_id)
  ) {
    return true;
  }
  if (
    intervention?.supporting_context?.sprint_id &&
    Number(sprintId) === Number(intervention.supporting_context.sprint_id)
  ) {
    return true;
  }
  return false;
}

function promptForRating() {
  const answer = window.prompt(
    "Rate your TrustBud: Very supportive, Supportive, Neutral, or Not very supportive. Leave blank to skip."
  );
  if (!answer) return "";
  const normalized = answer.trim().toLowerCase();
  if (normalized.startsWith("very")) return "very_supportive";
  if (normalized.startsWith("supportive")) return "supportive";
  if (normalized.startsWith("neutral")) return "neutral";
  if (normalized.startsWith("not")) return "not_supportive";
  return "";
}

function buildSprintFocusLine({ goalTitle, habitTitles = [], endLabel }) {
  const goal = String(goalTitle || "").trim() || "General consistency";
  const habits = (Array.isArray(habitTitles) ? habitTitles : []).filter(Boolean).slice(0, 2).join(" · ");
  const parts = [habits ? `${goal} · Counts for reports: ${habits}` : goal, String(endLabel || "").trim()].filter(Boolean);
  return parts.length ? parts.join(" · ") : "No Accountability focus yet";
}

function buildPartnerFocusLine(context, relationship) {
  const title = String(context?.sprint_goal?.title || "").trim();
  const summary = String(context?.sprint_goal?.summary || "").trim();
  const firstAction = String(context?.sprint_actions?.find((action) => action?.title)?.title || "").trim();
  const fallbackTitle = String(
    relationship?.primaryGoal?.title ||
      relationship?.vision?.title ||
      ""
  ).trim();
  return title || firstAction || summary || fallbackTitle || "";
}

export function useSprintWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetSprintId = searchParams.get("sprintId");
  const targetUserId = searchParams.get("userId");
  const targetConnectionId = searchParams.get("connectionId");
  const focusParam = searchParams.get("focus");

  const [trustbuds, setTrustbuds] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [reflectionToday, setReflectionToday] = useState(null);
  const [reflectionWeek, setReflectionWeek] = useState(null);
  const [dailyReportBoard, setDailyReportBoard] = useState(null);
  const [partnerContext, setPartnerContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionFeedback, setActionFeedback] = useState("");
  const [composerFocusKey, setComposerFocusKey] = useState(0);
  const initialComposerFocusHandledRef = useRef(false);

  const intervention = dashboardData?.intervention || null;

  const loadDashboardState = useCallback(async () => {
    try {
      const result = await fetchDashboard();
      setDashboardData(result);
    } catch {
      setDashboardData(null);
    }
  }, []);

  const loadTrustbuds = useCallback(async () => {
    try {
      const data = await apiFetch("/companions/shared-progress/");
      const list = Array.isArray(data) ? data.filter(isSprintWorkspaceCandidate) : [];
      setTrustbuds(list);
      setError("");
    } catch {
      setTrustbuds([]);
      setError("Unable to load Accountability.");
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadTrustbuds(), loadDashboardState()]);
  }, [loadDashboardState, loadTrustbuds]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      await refresh();
      if (mounted) setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.sourceSurface === "sprint") return;
      refresh();
    };
    window.addEventListener("trustbud:connections-changed", handler);
    window.addEventListener(BEHAVIOR_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener("trustbud:connections-changed", handler);
      window.removeEventListener(BEHAVIOR_CHANGED_EVENT, handler);
    };
  }, [refresh]);

  useEffect(() => {
    if (!actionFeedback) return undefined;
    const timer = window.setTimeout(() => setActionFeedback(""), 2800);
    return () => window.clearTimeout(timer);
  }, [actionFeedback]);

  const selectedTrustbud = useMemo(() => {
    if (!trustbuds.length) return null;
    const ranked = [...trustbuds].sort((a, b) => {
      const rank = (item) => {
        const sprintId = item?.active_sprint?.id || item?.connection?.sprint_id || null;
        const userId = item?.other_user?.id || null;
        const networkConnectionId = item?.connection?.network_connection_id || item?.id || null;
        if (targetConnectionId && String(networkConnectionId) === String(targetConnectionId)) return -3;
        if (targetSprintId && String(sprintId) === String(targetSprintId)) return -2;
        if (targetUserId && String(userId) === String(targetUserId)) return -1;
        if (isInterventionTarget(item, intervention)) return 0;
        return 1;
      };
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      const aStreak = Number(a?.summary?.shared_streak ?? 0);
      const bStreak = Number(b?.summary?.shared_streak ?? 0);
      return bStreak - aStreak;
    });
    return ranked[0] || null;
  }, [intervention, targetConnectionId, targetSprintId, targetUserId, trustbuds]);

  useEffect(() => {
    async function loadReflection() {
      const sprintId = selectedTrustbud?.active_sprint?.id || null;
      const partnerUserId = selectedTrustbud?.other_user?.id || null;
      if (!sprintId) {
        setReflectionToday(null);
        setReflectionWeek(null);
        setDailyReportBoard(null);
        return;
      }
      try {
        const [today, week, board] = await Promise.all([
          fetchSprintReflectionToday({ sprintId, userId: partnerUserId }),
          fetchSprintReflectionWeek({ sprintId, userId: partnerUserId }),
          fetchAccountabilityDailyReport(sprintId),
        ]);
        setReflectionToday(today || null);
        setReflectionWeek(week || null);
        setDailyReportBoard(board || null);
      } catch {
        setReflectionToday(null);
        setReflectionWeek(null);
        setDailyReportBoard(null);
      }
    }
    loadReflection();
  }, [selectedTrustbud]);

  useEffect(() => {
    const sprintId = selectedTrustbud?.active_sprint?.id || null;
    if (!sprintId) {
      setPartnerContext(null);
      return;
    }

    let active = true;
    async function loadPartnerContext() {
      try {
        const payload = await fetchSprintPartnerProfile(sprintId);
        if (active) {
          setPartnerContext(payload || null);
        }
      } catch {
        if (active) {
          setPartnerContext(null);
        }
      }
    }

    loadPartnerContext();
    return () => {
      active = false;
    };
  }, [
    selectedTrustbud?.active_sprint?.id,
  ]);

  const workspace = useMemo(() => {
    if (!selectedTrustbud) return null;

    const username = selectedTrustbud?.other_user?.username || "TrustBud";
    const connection = selectedTrustbud?.connection || {};
    const sprintId = selectedTrustbud?.active_sprint?.id || null;
    if (!sprintId) return null;
    const partnerUserId = selectedTrustbud?.other_user?.id || null;
    const connectionId = connection?.id || null;
    const networkConnectionId =
      selectedTrustbud?.network_connection?.id ||
      selectedTrustbud?.connection?.network_connection_id ||
      null;
    const interventionMatches = isInterventionTarget(selectedTrustbud, intervention);
    const explicitState = interventionMatches ? intervention?.state : null;
    const dominantIntervention = interventionMatches
      ? buildDominantInterventionViewModel(intervention)
      : null;
    const viewerActedToday = getViewerActedToday({
      behaviorSummary: dashboardData?.behavior_summary || null,
      summary: dashboardData?.summary || null,
      localFallback: Boolean(
        reflectionToday?.viewer_updated || reflectionToday?.viewer_noted || reflectionToday?.viewer_reflected
      ),
      sourceLabel: "sprint-web",
    });
    const partnerActedToday = getPartnerActedToday({
      relationship: selectedTrustbud,
      localFallback: Boolean(
        reflectionToday?.other_updated || reflectionToday?.other_noted || reflectionToday?.other_reflected
      ),
      sourceLabel: "sprint-web",
    });
    const sprintWindow = getSprintWindow({
      behaviorSummary: dashboardData?.behavior_summary || null,
      relationship: selectedTrustbud,
      sprint: selectedTrustbud?.active_sprint || null,
      connection,
      sourceLabel: "sprint-web",
    });
    const canRenew = Boolean(
      connection?.can_renew &&
        (connection?.status === "expired" || sprintWindow.isExpired || connection?.within_grace)
    );
    const sprintMode = selectedTrustbud?.active_sprint?.sprint_mode || connection?.sprint_mode || "support";
    const challengeScore = selectedTrustbud?.active_sprint?.challenge_score || selectedTrustbud?.workspace_summary?.challenge_score || null;
    const viewerChallengeScore = Number(challengeScore?.competitive?.viewer_score ?? challengeScore?.viewer?.showed_up_days ?? selectedTrustbud?.workspace_summary?.viewer_active_days_last_7 ?? 0);
    const partnerChallengeScore = Number(challengeScore?.competitive?.partner_score ?? challengeScore?.partner?.showed_up_days ?? selectedTrustbud?.workspace_summary?.partner_active_days_last_7 ?? 0);
    const sharedChallengeDays = Number(
      challengeScore?.support?.together_days ??
        selectedTrustbud?.workspace_summary?.days_both_updated_last_7 ??
        selectedTrustbud?.workspace_summary?.shared_active_days_last_7 ??
        0
    );
    const isCompetitiveChallenge = sprintMode === "challenge";
    const stateCopy = resolveSprintStateCopy({
      explicitState,
      reflectionToday,
      lastEvent: selectedTrustbud?.lastEvent,
      partnerName: username,
      viewerActedToday,
      partnerActedToday,
      sprintMode,
      sprintWindow,
      canRenew,
    });
    const ring = {
      ...buildMomentumRingData(
        reflectionWeek,
        reflectionToday,
        explicitState,
        selectedTrustbud?.workspace_summary,
        selectedTrustbud?.summary,
        dashboardData?.summary || null
      ),
      activeDaysLast7: isCompetitiveChallenge
        ? viewerChallengeScore
        : sharedChallengeDays,
      todayActive: isCompetitiveChallenge
        ? viewerActedToday || partnerActedToday
        : viewerActedToday && partnerActedToday,
      state: stateCopy.state,
      valueLabel: "",
      labelLineOne: isCompetitiveChallenge ? `You: ${viewerChallengeScore} of ${Number(challengeScore?.duration_days ?? 7)}` : "days",
      labelLineTwo: isCompetitiveChallenge ? `${username}: ${partnerChallengeScore}` : "together",
    };
    const status = connection?.status === "expired" || sprintWindow.isExpired
        ? "Expired"
        : "Active";
    const canonicalSummary = normalizeCanonicalSummary(
      dashboardData?.behavior_summary || dashboardData?.summary || {}
    );
    const lastUpdateSource =
      getPartnerLastActivityAt({
        relationship: selectedTrustbud,
      localFallback:
        reflectionWeek?.last_update_at_other ||
        connection?.last_update_at_other ||
        reflectionWeek?.last_note_at_other ||
        reflectionWeek?.last_reflection_at_other ||
        selectedTrustbud?.workspace_summary?.last_update_at_other ||
        null,
    });
    const targetDateLabel = formatDateLabel(
      selectedTrustbud?.primaryGoal?.due_date ||
        selectedTrustbud?.primaryGoal?.dueDate ||
        selectedTrustbud?.primaryGoal?.target_date
    );

    return {
      id: getRelationshipKey(selectedTrustbud),
      sprintId,
      partnerUserId,
      connectionId,
      partnerName: username,
      partnerImage: selectedTrustbud?.other_user?.profile_image || "",
      state: dominantIntervention?.state || stateCopy.state,
      stateLabel: dominantIntervention?.chip || stateCopy.chip,
      title: dominantIntervention?.title || stateCopy.title,
      body: dominantIntervention?.body || stateCopy.body,
      viewerCheckedInToday: viewerActedToday,
      partnerCheckedInToday: partnerActedToday,
      viewerStatusToday: challengeScore?.viewer?.today_status || (viewerActedToday ? "showed_up" : "pending"),
      partnerStatusToday: challengeScore?.partner?.today_status || (partnerActedToday ? "showed_up" : "pending"),
      todayStateKey: stateCopy.stateKey,
      sprintMode,
      modeLabel: sprintMode === "challenge" ? "Leaderboard on" : "Accountability",
      primaryActionKey: dominantIntervention?.primaryActionKey || "",
      primaryActionLabel: dominantIntervention?.primaryActionLabel || "",
      sprintPrimaryActionLabel: stateCopy.primaryActionLabel,
      sprintPrimaryActionRole: stateCopy.primaryActionRole,
      sprintSecondaryActionLabel: stateCopy.secondaryActionLabel,
      sprintSecondaryActionRole: stateCopy.secondaryActionRole,
      partnerContextLine: dominantIntervention?.partnerContextLine || "",
      supportingLine: dominantIntervention?.supportingLine || "",
      successMessage: dominantIntervention?.successMessage || "",
      ring,
      challengeScore,
      quickReplies: buildSprintQuickReplies({ stateKey: stateCopy.stateKey, sprintMode }),
      recentMovement: buildRecentMovementItems({
        reflectionToday,
        workspaceSummary: selectedTrustbud?.workspace_summary,
        lastEvent: selectedTrustbud?.lastEvent,
        partnerName: username,
        viewerActedToday,
        partnerActedToday,
      }),
      weekPattern: Array.isArray(challengeScore?.week)
        ? challengeScore.week.map((item) => ({
            key: String(item?.date || ""),
            label: new Date(`${item?.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1),
            viewerActive: ["showed_up", "small_version"].includes(String(item?.viewer_status || "")) || (sprintMode !== "challenge" && item?.viewer_status === "reset"),
            partnerActive: ["showed_up", "small_version"].includes(String(item?.partner_status || "")) || (sprintMode !== "challenge" && item?.partner_status === "reset"),
            sharedActive: Boolean(item?.both_showed_up),
          }))
        : buildWeekPattern(
            reflectionWeek,
            selectedTrustbud?.workspace_summary,
            selectedTrustbud?.summary,
            dashboardData?.summary || null,
            {
              startDate: selectedTrustbud?.active_sprint?.start_date || connection?.start_date || null,
              endDate: selectedTrustbud?.active_sprint?.end_date || connection?.end_date || null,
            }
          ),
      partnerStatus: status,
      partnerLastMovementLabel: lastUpdateSource
        ? `Last activity ${formatRelativeDayWord(lastUpdateSource)}`
        : "Hasn't shown up recently",
      focusLine: buildSprintFocusLine({
        goalTitle: selectedTrustbud?.challenge_focus?.goal_title || selectedTrustbud?.challenge_focus?.display_label || selectedTrustbud?.primaryGoal?.title || "",
        habitTitles: selectedTrustbud?.challenge_focus?.habit_titles || [],
        endLabel: sprintWindow.endChipLabel || sprintWindow.endLabel || targetDateLabel,
      }),
      partnerFocusLine:
        (selectedTrustbud?.partner_challenge_focus?.habit_titles?.length
          ? `${selectedTrustbud?.partner_challenge_focus?.goal_title || selectedTrustbud?.partner_challenge_focus?.display_label} · Counts for reports: ${selectedTrustbud.partner_challenge_focus.habit_titles.slice(0, 2).join(" · ")}`
          : selectedTrustbud?.partner_challenge_focus?.display_label ||
            selectedTrustbud?.partner_challenge_focus?.goal_title) ||
        buildPartnerFocusLine(partnerContext, selectedTrustbud),
      lifecycleSummary: sprintWindow.lifecycleSummary,
      startLabel: formatDateLabel(selectedTrustbud?.active_sprint?.start_date || connection?.start_date),
      endLabel: sprintWindow.endLabel,
      endChipLabel: sprintWindow.endChipLabel,
      visionTitle: selectedTrustbud?.vision?.title || "",
      canRenew,
      renewLabel: connection?.viewer_requested_renewal
        ? connection?.other_requested_renewal
          ? "Renewed"
          : "Renewal requested"
        : "Renew Accountability",
      canClose: Boolean(connection?.can_close),
      actionsOpenToday: canonicalSummary.actionsOpenToday,
      networkConnectionId,
      isLocked: false,
    };
  }, [dashboardData?.behavior_summary, dashboardData?.summary, intervention, partnerContext, reflectionToday, reflectionWeek, selectedTrustbud]);

  const actions = useMemo(() => {
    if (!workspace) return null;

    return {
      openNetwork: () => navigate("/activity?tab=messages"),
      openConnections: () => navigate("/connections"),
      openPlan: () => navigate("/plan"),
      openPartnerFocus: () =>
        navigate(
          `/network/person/${workspace.networkConnectionId || workspace.connectionId || ""}?sprintId=${workspace.sprintId || ""}&source=sprint`
        ),
      messagePartner: () => setComposerFocusKey((current) => current + 1),
      viewWeek: () => {
        const target = document.getElementById("sprint-week-pattern");
        target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      },
      doOneStep: () =>
        navigate(
          workspace.actionsOpenToday > 0
            ? "/?focus=action-picker&source=sprint"
            : "/?source=sprint"
        ),
      showUpStatus: async (status) => {
        const result = await submitChallengeShowUp(workspace.sprintId, status);
        setActionFeedback(result?.message || "Report submitted.");
        window.dispatchEvent(new CustomEvent(BEHAVIOR_CHANGED_EVENT, { detail: { sourceSurface: "sprint" } }));
        await refresh();
      },
      submitTimerReport: async () => {
        await submitAccountabilityTimerReport(workspace.sprintId, 300);
        setActionFeedback("Timer proof added.");
        window.dispatchEvent(new CustomEvent(BEHAVIOR_CHANGED_EVENT, { detail: { sourceSurface: "sprint" } }));
        await refresh();
      },
      attachPhotoProof: async (file) => {
        if (!file) return;
        await submitAccountabilityPhotoProof(workspace.sprintId, file);
        setActionFeedback("Photo proof attached.");
        window.dispatchEvent(new CustomEvent(BEHAVIOR_CHANGED_EVENT, { detail: { sourceSurface: "sprint" } }));
        await refresh();
      },
      acknowledgePartnerReport: dailyReportBoard?.partner?.report?.id
        ? async () => {
            await acknowledgeAccountabilityReport(workspace.sprintId, dailyReportBoard.partner.report.id);
            setActionFeedback("Acknowledged.");
            await refresh();
          }
        : null,
      renew: async () => {
        const rating = promptForRating();
        await renewTrustBudRelationship({
          connectionId: workspace.connectionId,
          userId: workspace.partnerUserId,
          rating,
        });
        setActionFeedback("Renewal request sent.");
        await refresh();
      },
      close: async () => {
        const confirmed = window.confirm(
          "Do not renew this Accountability? This ends renewal for this agreement. Your TrustBud connection stays in Connections."
        );
        if (!confirmed) return;
        const rating = promptForRating();
        await closeTrustBudRelationship({
          connectionId: workspace.connectionId,
          userId: workspace.partnerUserId,
          rating,
        });
        setActionFeedback("Accountability ended.");
        await refresh();
      },
      disconnect: async () => {
        const confirmed = window.confirm(
          "End this Accountability? This ends the active agreement. You will stay connected as TrustBuds."
        );
        if (!confirmed) return;
        const rating = promptForRating();
        await disconnectTrustBudRelationship({
          connectionId: workspace.connectionId,
          userId: workspace.partnerUserId,
          rating,
        });
        window.dispatchEvent(new Event("trustbud:connections-changed"));
        await refresh();
      },
      block: async () => {
        const confirmed = window.confirm(`Block ${workspace.partnerName}? This will hide their content.`);
        if (!confirmed) return;
        await blockUser(workspace.partnerUserId);
        window.dispatchEvent(new Event("trustbud:connections-changed"));
        await refresh();
      },
    };
  }, [dailyReportBoard, navigate, refresh, workspace]);

  useEffect(() => {
    if (focusParam !== "composer") return;
    if (initialComposerFocusHandledRef.current) return;
    if (!workspace || workspace.isLocked) return;
    initialComposerFocusHandledRef.current = true;
    setComposerFocusKey((current) => current + 1);
  }, [focusParam, workspace]);

  return {
    loading,
    error,
    actionFeedback,
    intervention,
    workspace,
    actions,
    composerFocusKey,
    refresh,
    openConnections: () => navigate("/connections"),
    openNetwork: () => navigate("/activity?tab=messages"),
  };
}
